import {
  GetHeadquartersJobsRequest,
  Int32StringValueTuple,
  LostFoundColorPublicResponse,
  LostFoundContentPublicResponse,
  getContactsUsTypes,
  getLostFoundColors,
  getLostFoundContent,
  postCareerAllHeadquartersJobs,
} from "api"
import {
  GetComponentQuery,
  GetPageByRouteQuery,
  getClient,
  fetchAllComponents,
} from "apollo"
import GET_COMP_FOR_GEN from "apollo/queries/GET_COMPONENTS_FOR_GEN"
import GET_PAGE_BY_ROUTE from "apollo/queries/GET_PAGE_BY_ROUTE"
import {
  Culture,
  HistoryData,
  TrafficUpdate,
  extractProperty,
  getBreadCrumbs,
  transformHistoryData,
  transformMany,
} from "utils"

export type BlockDataLoaderContext = {
  locale: string
  culture: Culture
  pageData: GetPageByRouteQuery
}

export type BlockExtraData = {
  historyData?: HistoryData[]
  trafficUpdates?: TrafficUpdate[]
  breadCrumbs?: ReturnType<typeof getBreadCrumbs>
  stationsData?: GetPageByRouteQuery
  recommendationsData?: GetPageByRouteQuery
  contactTypesData?: Int32StringValueTuple[]
  lostFoundContentData?: LostFoundContentPublicResponse[]
  lostFoundColorsData?: LostFoundColorPublicResponse[]
  formDynamicTextTooltip?: string
  jobResponse?: any
}

type DataLoader = (context: BlockDataLoaderContext) => Promise<BlockExtraData>

export const blockDataLoaders: Record<string, DataLoader> = {
  timeline: async ({ culture }): Promise<BlockExtraData> => {
    const historyPath = "/pages/egged-group/egged-public-transportation/history"

    const { data } = await getClient().query<GetComponentQuery>({
      query: GET_COMP_FOR_GEN,
      variables: { route: historyPath, culture },
    })

    return {
      historyData: transformHistoryData(data),
    }
  },

  trafficUpdates: async ({ culture }): Promise<BlockExtraData> => {
    const trafficUpdatesPath = "/pages/traffic-updates"

    const data = await fetchAllComponents(trafficUpdatesPath, culture)

    return {
      trafficUpdates: transformMany(data),
    }
  },

  heroBanner: async ({ pageData, locale }): Promise<BlockExtraData> => {
    return {
      breadCrumbs: getBreadCrumbs(pageData, locale),
    }
  },

  MultyURLsTitleAndText: async ({ culture }): Promise<BlockExtraData> => {
    const stationsPath =
      "/pages/information-for-passengers/rav-kav-and-payment-apps/rav-kav-stations"

    const { data } = await getClient().query<GetPageByRouteQuery>({
      query: GET_PAGE_BY_ROUTE,
      variables: { route: stationsPath, culture },
    })

    return {
      stationsData: data,
    }
  },

  employeeRecommendations: async ({ culture }): Promise<BlockExtraData> => {
    const recommendationsPath = "/component/employee-recommendations"
    const { data } = await getClient().query<GetPageByRouteQuery>({
      query: GET_PAGE_BY_ROUTE,
      variables: { route: recommendationsPath, culture },
    })
    console.log(recommendationsPath, "23fwefwefwef")
    return {
      recommendationsData: data,
    }
  },
  recommendationsBlock: async ({ culture }): Promise<BlockExtraData> => {
    const recommendationsPath = "/component/employee-recommendations"
    const { data } = await getClient().query<GetPageByRouteQuery>({
      query: GET_PAGE_BY_ROUTE,
      variables: { route: recommendationsPath, culture },
    })
    console.log(recommendationsPath, "23fwefwefwef")
    return {
      recommendationsData: data,
    }
  },

  InquiriesAndRequests: async ({
    locale,
    culture,
  }): Promise<BlockExtraData> => {
    const formDynamicTextPath = "/he/component/form-dynamic-text"

    const [
      contactTypes,
      lostFoundContent,
      lostFoundColors,
      formDynamicTextData,
    ] = await Promise.all([
      getContactsUsTypes(locale),
      getLostFoundContent(locale),
      getLostFoundColors(locale),
      getClient().query<GetPageByRouteQuery>({
        query: GET_PAGE_BY_ROUTE,
        variables: { route: formDynamicTextPath, culture },
      }),
    ])

    // Extract tooltip text from formDynamicText
    const formDynamicTextTooltip =
      extractProperty(formDynamicTextData.data.page, "tooltipText") || ""

    return {
      contactTypesData: contactTypes.data.body,
      lostFoundContentData: lostFoundContent.data.body,
      lostFoundColorsData: lostFoundColors.data.body,
      formDynamicTextTooltip,
    }
  },

  contactUs: async (): Promise<BlockExtraData> => {
    // Uses CMS block data only, no external data needed
    return {}
  },

  careerFormSection: async ({ culture }): Promise<BlockExtraData> => {
    const formDynamicTextPath = "/he/component/form-dynamic-text"

    const { data } = await getClient().query<GetPageByRouteQuery>({
      query: GET_PAGE_BY_ROUTE,
      variables: { route: formDynamicTextPath, culture },
    })
    console.log(data, "dataformDynamicTextPath")
    // Extract tooltip text from formDynamicText
    const formDynamicTextTooltip =
      extractProperty(data.page, "tooltipText") || ""

    return {
      formDynamicTextTooltip,
    }
  },

  jobDisplay: async (): Promise<BlockExtraData> => {
    const requestData: GetHeadquartersJobsRequest = {
      searchTerm: "",
      filters: [],
      requestPage: 0,
      pageSize: 10,
    }

    try {
      const jobResponse = await postCareerAllHeadquartersJobs(requestData)
      return {
        jobResponse: jobResponse?.data?.body,
      }
    } catch (error) {
      console.error("Error fetching headquarters jobs:", error)
      return {
        jobResponse: null,
      }
    }
  },
}

export function getBlockTypesNeedingData(
  blocks: Array<{ blockType: string }>,
): string[] {
  const uniqueTypes = new Set<string>()

  for (const block of blocks) {
    if (blockDataLoaders[block.blockType]) {
      uniqueTypes.add(block.blockType)
    }
  }

  return Array.from(uniqueTypes)
}

export async function loadBlockData(
  blockTypes: string[],
  context: BlockDataLoaderContext,
): Promise<BlockExtraData> {
  const loaders = blockTypes
    .map((type) => blockDataLoaders[type])
    .filter(Boolean)

  if (loaders.length === 0) {
    return {}
  }

  const results = await Promise.all(loaders.map((loader) => loader(context)))

  return results.reduce((acc, result) => ({ ...acc, ...result }), {})
}
